import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight, Mail } from "lucide-react";
import { attributionPayload } from "@/utils/attribution";

const WAITLIST_ENDPOINT = "https://bill-ready-1.emergent.host/api/waitlist";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const WaitlistForm = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!isValidEmail(email)) {
      setErrorMsg("Please enter a valid email address.");
      toast.error("Please enter a valid email address.");
      return;
    }

    setStatus("submitting");
    try {
      const attribution = attributionPayload();
      const res = await fetch(WAITLIST_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          source: "landing_page",
          // Traffic attribution — ref + full UTM set + landing_url + session_id
          // Only sent when present on the entry URL.
          attribution,
          // Also mirror the individual keys at the top level for backends that
          // don't nest attribution.
          ...attribution,
        }),
      });

      if (!res.ok) {
        let msg = `Request failed (${res.status}).`;
        try {
          const data = await res.json();
          if (data?.message) msg = data.message;
          else if (data?.detail) msg = typeof data.detail === "string" ? data.detail : msg;
        } catch (_) {}
        throw new Error(msg);
      }

      setStatus("success");
      toast.success("You're on the list! We'll be in touch.");
    } catch (err) {
      setStatus("error");
      const message = err?.message || "Something went wrong. Please try again.";
      setErrorMsg(message);
      toast.error(message);
    }
  };

  if (status === "success") {
    return (
      <div
        className="sn-glass-strong rounded-2xl p-6 md:p-7 flex items-start gap-4"
        data-testid="waitlist-success"
      >
        <div
          className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
          style={{
            background: "linear-gradient(140deg,#DFFBEC 0%,#B7F1D8 100%)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
          }}
        >
          <CheckCircle2 className="text-[#15A76C]" size={22} />
        </div>
        <div>
          <h3 className="font-semibold text-[#0B1D3A] text-lg" style={{ fontFamily: "Sora, Inter, sans-serif" }}>
            You're on the list.
          </h3>
          <p className="text-sm text-[#33406B] mt-1">
            We'll email <span className="font-medium text-[#0B1D3A]">{email}</span> as soon as SpendNova&trade; opens up spots. Stand by.
          </p>
          <button
            type="button"
            onClick={() => {
              setEmail("");
              setStatus("idle");
            }}
            className="mt-3 text-sm font-semibold text-[#15A76C] hover:text-[#0f8757]"
            data-testid="waitlist-add-another"
          >
            Add another email →
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="sn-glass-strong rounded-2xl p-4 md:p-5"
      data-testid="waitlist-form"
      noValidate
    >
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Mail
            size={18}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8b93b3] pointer-events-none"
          />
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourbase.mil"
            aria-label="Email address"
            className="sn-input w-full pl-10 pr-4 py-3.5 rounded-xl text-[15px] font-medium placeholder:text-[#a1a8c4]"
            data-testid="waitlist-email-input"
            disabled={status === "submitting"}
          />
        </div>
        <button
          type="submit"
          className="sn-btn-primary inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl text-[15px] font-semibold whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed"
          disabled={status === "submitting"}
          data-testid="waitlist-submit"
        >
          {status === "submitting" ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Sending…
            </>
          ) : (
            <>
              Join Waitlist
              <ArrowRight size={18} />
            </>
          )}
        </button>
      </div>
      {errorMsg && (
        <p className="mt-3 text-xs text-[#c94a4a] font-medium" data-testid="waitlist-error">
          {errorMsg}
        </p>
      )}
      <p className="mt-3 text-xs text-[#5b6789]">
        No spam. We only email about early access & security-related updates.
      </p>
    </form>
  );
};

export default WaitlistForm;
